/*
 * Copyright (C) 2022 Hal Perkins.  All rights reserved.  Permission is
 * hereby granted to students registered for University of Washington
 * CSE 331 for use solely during Winter Quarter 2022 for purposes of
 * the course.  No other use, copying, distribution, or modification
 * is permitted without prior written consent. Copyrights for
 * third-party components of this work must be honored.  Instructors
 * interested in reusing these course materials should contact the
 * author.
 */

import React, {Component} from 'react';
import MapComponent from "./MapComponent";
import MapLine from "./MapLine";
import Select from "react-select";
import {SingleValue} from 'react-select';
import {rgbToHex} from "./Color";
import InstructionsModal from "./InstructionsModal";
import Spinner from 'react-bootstrap/Spinner';

import "./App.css";
import 'bootstrap/dist/css/bootstrap.min.css';

interface AppState {
    startBuilding: string; // the current selection in the first dropdown list; represents the start building
    endBuilding: string; // the current selection in the second dropdown list; represents the end building
    route: Edge[]; // the route that has been found, to be displayed to the user
                   // (i.e. the lines to be drawn in MapLine)
    buildings: {shortName: any, longName: any}[]; // the buildings of the map
    shortToLongNames: Map<string, any>;
    pathInfo: string; // path info information
    red: number; // red in path color
    green: number; // green in path color
    blue: number; // blue in path color
    instructionsOpen: boolean; // whether the info/instructions box is open
    isLoading: boolean; // whether the building options are being loaded
    showAllBuildings: boolean;
    buildingPoints: Map<string, Coordinate>;
}

interface Edge {
    start: Coordinate;
    end: Coordinate;
    cost: number;
}

interface Coordinate {
    x: number;
    y: number;
}

interface StringOption {
    label: string;
    value: string;
}

const fetchBuildings = fetch('https://uw-pathfinder.onrender.com/list-buildings?');
const fetchBuildingCoords = fetch('https://uw-pathfinder.onrender.com/get-coords-of-buildings?');

class App extends Component<{}, AppState> {
    constructor(props: any) {
        super(props);
        this.state = {
            startBuilding: "Select...",
            endBuilding: "Select...",
            route: [],
            buildings: [],
            shortToLongNames: new Map<string, any>(),
            pathInfo: "",
            red: 0,
            green: 0,
            blue: 0,
            instructionsOpen: true,
            isLoading: true,
            showAllBuildings: false,
            buildingPoints: new Map<string, Coordinate>(),
        };
    }

    componentDidMount() {
        Promise.all([fetchBuildings, fetchBuildingCoords])
            .then(async ([response, response2]) => {
                if (!response.ok || !response2.ok) {
                    throw new Error(`HTTP error! status: ${response.status} or ${response2.status}`);
                }
                const parsedObj = await response.json();

                if (!parsedObj || typeof parsedObj !== 'object') {
                    throw new Error('Invalid response format');
                }

                let tempBuildings: {shortName: any, longName: any}[] = [];
                let tempNames = new Map<string, any>();
                tempBuildings.push({shortName: "", longName: "Select..."});
                for (let [k, v] of Object.entries(parsedObj)) {
                    tempBuildings.push({shortName: k, longName: v});
                    tempNames.set(k, v);
                }
                this.setState({
                    buildings: tempBuildings,
                    shortToLongNames: tempNames,
                });

                const parsedObj2 = await response2.json();

                if (!parsedObj2 || typeof parsedObj2 !== 'object') {
                    throw new Error('Invalid response format');
                }

                let tempBuildingPoints = new Map<string, Coordinate>();
                for (let [k, v] of Object.entries(parsedObj2)) {
                    if (v && typeof v === 'object' && 'x' in v && 'y' in v) {
                        tempBuildingPoints.set(k, {
                            x: Number(v.x),
                            y: Number(v.y)
                        });
                    }
                }
                this.setState({
                    isLoading: false,
                    buildingPoints: tempBuildingPoints,
                });

            }).catch((error) => {
                console.error('Error fetching building data:', error);
                this.setState({ isLoading: false });
            });
    }

    toggleBuildings = () => {
        this.setState(prevState => ({
            showAllBuildings: !prevState.showAllBuildings
        }));
    }

    findPath = async (start: any, end: any) => {
        console.log("Finding path from", start, "to", end);

        if (start === "" || end === "") {
            this.setState({
                route: [],
                pathInfo: "",
            });
            alert("Please select both a start and end building.");
            return;
        }

        try {
            let response = await fetch('https://uw-pathfinder.onrender.com/find-path?start=' + start + '&end=' + end);
            let parsedObj = await response.json();
            if (parsedObj === null) {
                this.setState({
                    route: [],
                    pathInfo: "",
                });
                return;
            } else {
                // go through the path that was returned from the server
                // send each "edge" in the path to MapLines to be drawn
                let path = parsedObj['path'];
                let lines: any[] = [];

                for (let i = 0; i < path.length; i++) {
                    const currEdge: Edge = path[i];

                    // get the start
                    const currEdgeStart = currEdge["start"];
                    const currEdgeStartX = currEdgeStart["x"];
                    const currEdgeStartY = currEdgeStart["y"];

                    // get the end of the path
                    const currEdgeEnd = currEdge["end"];
                    const currEdgeEndX = currEdgeEnd["x"];
                    const currEdgeEndY = currEdgeEnd["y"];

                    let hexColor= rgbToHex(this.state.red, this.state.green, this.state.blue);
                    lines.push(<MapLine key={i}
                                        color={hexColor}
                                        x1={currEdgeStartX} y1={currEdgeStartY}
                                        x2={currEdgeEndX} y2={currEdgeEndY}/>);
                }
                let cost: number = parsedObj["cost"];
                let mi = Math.round(cost / 5280 * 100) / 100;
                let hrs = mi * (1 / 2.5); // mi * (hr / mi)
                let min = Math.round((hrs * 60 * 10) / 10);
                let longStart = this.state.shortToLongNames.get(start);
                let longEnd = this.state.shortToLongNames.get(end);
                let value = longStart +  " to " + longEnd + ": " + mi + " mi (" + min + " min walk)";
                this.setState({
                    route: lines,
                    pathInfo: value,
                });
            }
        } catch (e) {
            console.error('Error in finding the path:', e);
        }
    }

    changeStart = (option: SingleValue<{value: string, label: string}>) => {
        this.setState({
            startBuilding: option ? (option as StringOption).value : "",
            route: []
        });
    }

    changeEnd = (option: SingleValue<{value: string, label: string}>) => {
        this.setState({
            endBuilding: option ? (option as StringOption).value : "",
            route: []
        });
    }

    changeRed = async (e: any) => {
        this.setState({
            red: e.target.value
        });
        if (this.state.startBuilding !== "Select..."
            && this.state.endBuilding !== "Select...") {
            this.findPath(this.state.startBuilding, this.state.endBuilding);
        }
    }

    changeGreen = (e: any) => {
        this.setState({
            green: e.target.value
        });
        if (this.state.startBuilding !== "Select..."
            && this.state.endBuilding !== "Select...") {
            this.findPath(this.state.startBuilding, this.state.endBuilding);
        }
    }

    changeBlue = (e: any) => {
        this.setState({
            blue: e.target.value
        });
        if (this.state.startBuilding !== "Select..."
            && this.state.endBuilding !== "Select...") {
            this.findPath(this.state.startBuilding, this.state.endBuilding);
        }
    }

    showInstructions = () => {
        this.setState({
            instructionsOpen: !this.state.instructionsOpen,
        });
    };

    render() {
        const { buildings, isLoading } = this.state;

        if (isLoading) {
            return (
              <div className="text-center" style={{ padding: '50px' }}>
                <div>
                    <Spinner animation="border" role="status"/>
                </div>
                <div>
                    <span style={{ color: 'black' }}>Loading...</span>
                </div>
              </div>
            );
        }

        const buildingOptions = buildings.map(option => ({
            label: option.longName,
            value: option.shortName
        }));

        let selectedStart = buildingOptions.find(
            option => option.label === this.state.startBuilding);

        let selectedEnd = buildingOptions.find(
            option => option.label === this.state.endBuilding);

        return (
            <div>
                {this.state.instructionsOpen ?
                    <InstructionsModal toggle={this.showInstructions} /> : null}
                <MapComponent 
                    route={this.state.route}
                    start={this.state.startBuilding}
                    end={this.state.endBuilding}
                    showAllBuildings={this.state.showAllBuildings}
                    buildingPoints={this.state.buildingPoints}
                    shortToLongNames={this.state.shortToLongNames}/>
                <div id="menu">
                    <div id="start-building">
                        <h3 id="start-building-title">START:</h3>
                        <Select
                            value={selectedStart}
                            options={buildingOptions}
                            onChange={this.changeStart}
                            isSearchable={true}
                        />
                    </div>
                    <div id="end-building">
                        <h3 id="end-building-title">END:</h3>
                        <Select
                            value={selectedEnd}
                            options={buildingOptions}
                            onChange={this.changeEnd}
                            isSearchable={true}
                        />
                    </div>
                    <div className="menu-buttons">
                        <button 
                            className="action-button"
                            id="find-path-button"
                            onClick={() => {
                                if (this.state.startBuilding !== "Select..." && 
                                    this.state.endBuilding !== "Select...") {
                                    this.findPath(this.state.startBuilding, this.state.endBuilding);
                                }
                            }}
                        >
                            FIND PATH
                        </button>
                        
                        <button 
                            className="action-button"
                            id="toggle-buildings-button"
                            onClick={this.toggleBuildings}
                        >
                            {this.state.showAllBuildings ? 'HIDE BUILDINGS' : 'SHOW BUILDINGS'}
                        </button>
                        
                        <button 
                            className="action-button"
                            id="info-button"
                            onClick={this.showInstructions}
                        >
                            INFO
                        </button>
                    </div>
                    <div id="adjust-color">
                        <h4>Adjust color:</h4>
                        <div className="color-adjustment">
                            <div className="slider-group">
                                <span className="label">R:</span>
                                <input
                                    type="range"
                                    step={1}
                                    min={0}
                                    max={255}
                                    value={this.state.red}
                                    onChange={e => this.changeRed(e)}
                                />
                            </div>
                            <div className="slider-group">
                                <span className="label">G:</span>
                                <input
                                    type="range"
                                    step={1}
                                    min={0}
                                    max={255}
                                    value={this.state.green}
                                    onChange={e => this.changeGreen(e)}
                                />
                            </div>
                            <div className="slider-group">
                                <span className="label">B:</span>
                                <input
                                    type="range"
                                    step={1}
                                    min={0}
                                    max={255}
                                    value={this.state.blue}
                                    onChange={e => this.changeBlue(e)}
                                />
                            </div>
                        </div>
                    </div>
                    <p id="path-info">{this.state.pathInfo}</p>
                </div>
            </div>
        );
    }
}
export default App;
